import { ConfigDrivenFormComponent } from 'features/mfe/components/ConfigDrivenFormComponent';
import { ExtendedFieldTemplate } from 'features/mfe/components/ExtendedFieldTemplate';
import { getFlavorIndexFromFinalFormName } from 'features/traffic/campaigns/content/generic.util';
import { useCampaignConfig } from 'features/traffic/campaigns/hooks/useCampaignConfig';
import type { FlavorPath } from 'features/traffic/campaigns/mcc.types';
import { CopyPasteContentModalWrapper } from 'features/traffic/campaigns/TPCampaignContent/CopyPasteContentModal';
import type { TrafficCampaignFlavor } from 'generated/graphql';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import type { ArrayAction, ConfigDrivenFormProps } from 'types/JourneysDeclarativeUI';
import { useDebounceCallback } from 'usehooks-ts';
import { useGetFieldPath } from 'utils/pathing';
import type { TContent } from '../types';
import {
  AltTextWithAutoGenerate,
  ColorPicker,
  CreatableSelect,
  HiddenServiceUrlWidget,
  LinkBuilder,
  LinkRefTagBuilder,
  LinkUrlBuilder,
  MediaPicker,
} from './custom-widgets';
import { type FormRef, formRefRegistry } from './FormRefRegistry';
import { useArraySyncHandler } from './useArraySyncHandler';
import { useExternalFormDataKey } from './useExternalFormDataKey';
import { useDMCCFieldSharingConfig, useVariableSharingPropagation } from './variable-sharing';

/*
 * Temporarily disable validation in our Cypress tests.
 * See this doc for full explanation: https://quip-amazon.com/1GwPANa8eTUW/RJSF-Validation-and-Remount-Issue
 * SIM ticket tracking: https://t.corp.amazon.com/P319708907
 */
const SKIP_VALIDATION = window.Cypress && window.__DISABLE_FORM_VALIDATION__;

// Static objects to prevent unnecessary recreations
const STATIC_WIDGETS = {
  altTextWithAutoGenerateWidget: AltTextWithAutoGenerate,
  colorPicker: ColorPicker,
  creatableSelect: CreatableSelect,
  hiddenServiceUrl: HiddenServiceUrlWidget,
  linkRefTagWidget: LinkRefTagBuilder,
  linkUrlWidget: LinkUrlBuilder,
  linkWidget: LinkBuilder,
  mediaUploadWidget: MediaPicker,
} as const;

const STATIC_TEMPLATES = {
  FieldTemplate: ExtendedFieldTemplate,
} as const;

const UI_SUBMIT_OPTIONS = {
  'ui:submitButtonOptions': {
    norender: true,
  },
} as const;

/**
 * React component for rendering config-driven DMCC content editing forms with variable sharing support.
 *
 * Provides a sophisticated form interface for editing DMCC (Dynamic Multi-Creative Campaign) content
 * using JSON Schema-driven configuration. Integrates with the DMCC field sharing system to enable
 * variable synchronization across campaign flavors, supporting real-time propagation of field changes
 * and array operations. The component uses optimized field-level subscriptions to minimize re-renders
 * and includes debounced change handling for improved performance.
 *
 * Key features include:
 * - JSON Schema-based form rendering with custom widgets (LinkBuilder, MediaPicker)
 * - DMCC field sharing configuration with BRD evaluation for variable propagation
 * - Array synchronization across multiple campaign flavors
 * - Optimized re-render performance through selective field subscriptions
 * - Copy/paste functionality for content transfer between flavors
 * - Debounced change handling to prevent excessive form updates
 *
 * The component automatically handles form data synchronization, ensuring that changes made in one
 * flavor are appropriately propagated to other flavors based on the configured sharing rules and
 * BRD evaluation results.
 *
 * @param name - Field name path for the current flavor (e.g., "flavors[0]") used for form field binding and flavor index extraction
 * @returns JSX element containing the copy/paste modal wrapper and the config-driven form component
 */
export const ConfigDrivenContentEditor: FC<{
  name: string;
}> = ({ name }) => {
  const { contentSection } = useCampaignConfig();
  const { jsonSchema, uiSchema, customValidate } = contentSection.contentConfiguration ?? {};

  const getContent = useGetFieldPath<TContent>(name);
  const formApi = useForm();

  // Use field-level subscriptions instead of entire form state
  // This dramatically reduces re-renders by only subscribing to specific fields
  const {
    input: { value: formData },
  } = useField(getContent('content'), {
    subscription: {
      value: true,
    },
  });
  const {
    input: { value: formContext },
  } = useField(getContent('scheduling'), {
    subscription: {
      value: true,
    },
  });
  const {
    input: { value: flavors },
  } = useField<TrafficCampaignFlavor[]>('flavors', {
    subscription: {
      value: true,
    },
  });

  const currentFlavorIndex = useMemo(
    () => getFlavorIndexFromFinalFormName(name),
    [
      name,
    ],
  );

  // DMCC field sharing configuration management
  const fieldSharingConfig = useDMCCFieldSharingConfig();

  const { syncArrayActionAcrossFlavors } = useArraySyncHandler({
    currentFlavorIndex,
    fieldSharingConfig,
    flavors,
  });

  const { applyVariableSharingPropagation } = useVariableSharingPropagation({
    currentFlavorIndex,
    flavors,
    uiSchema: uiSchema as never,
  });

  const debouncedValidate = useDebounceCallback(
    useCallback(() => {
      formRefRegistry.get(currentFlavorIndex)?.validateForm();
      // Trigger final form validation
      formApi.blur(getContent('content'));
    }, [
      currentFlavorIndex,
      formApi.blur,
      getContent,
    ]),
  );

  const setFormRefForFlavor = useCallback(
    (ref: FormRef | null) => {
      formRefRegistry.set(currentFlavorIndex, ref);
      if (ref && !SKIP_VALIDATION) {
        debouncedValidate();
      }
    },
    [
      currentFlavorIndex,
      debouncedValidate,
    ],
  );

  // Generate stable key for form remounting on external formData changes
  // This solves RJSF UI not updating when formData is changed by variable sharing or array sync
  // Use flavor-specific prefix to ensure uniqueness across multiple form instances
  const { key: formKey, markUserChange } = useExternalFormDataKey(formData, `flavor-${currentFlavorIndex}`);

  const onChange: ConfigDrivenFormProps['onChange'] = useDebounceCallback(
    useCallback(
      (
        {
          formData: newFormData,
        }: {
          formData: unknown;
        },
        arrayActions: ArrayAction[],
      ) => {
        // Mark this as a user-initiated change to prevent form remount
        markUserChange();

        // Use current formData from subscription instead of stale ref
        // This ensures we always have accurate previous state, even after external updates
        const previousFormData = formData;

        formApi.change(getContent('content'), newFormData);
        arrayActions.forEach((arrayAction) => syncArrayActionAcrossFlavors(newFormData, arrayAction));
        applyVariableSharingPropagation({
          dmccOverrideMap: fieldSharingConfig.dmccOverrideMap,
          newFormData,
          previousFormData,
        });

        if (!SKIP_VALIDATION) debouncedValidate();
      },
      [
        applyVariableSharingPropagation,
        debouncedValidate,
        fieldSharingConfig.dmccOverrideMap,
        formApi.change,
        formData,
        getContent,
        markUserChange,
        syncArrayActionAcrossFlavors,
      ],
    ),
  );

  useEffect(
    () => () => onChange.flush(),
    [
      onChange,
    ],
  );

  const arrayItemExpandedStateMap = useMemo(() => new Map<string, boolean>(), []);

  // Memoize form props to prevent unnecessary re-renders
  const formProps: ConfigDrivenFormProps = useMemo(
    () => ({
      formContext: {
        ...formContext,
        applyVariableSharingPropagation,
        arrayItemExpandedStateMap,
        currentFlavorIndex,
        dmccConfig: fieldSharingConfig,
        flavors,
      },
      formData,
      liveValidate: false,
      onChange,
      schema: jsonSchema ?? {},
      templates: STATIC_TEMPLATES,
      uiSchema: {
        ...(uiSchema ?? {}),
        ...UI_SUBMIT_OPTIONS,
      },
      widgets: STATIC_WIDGETS,
      ...(customValidate && {
        customValidate,
      }),
    }),
    [
      applyVariableSharingPropagation,
      currentFlavorIndex,
      customValidate,
      arrayItemExpandedStateMap,
      fieldSharingConfig,
      flavors,
      formContext,
      formData,
      jsonSchema,
      onChange,
      uiSchema,
    ],
  );

  return (
    <>
      <CopyPasteContentModalWrapper pathToFlavor={name as FlavorPath} />
      <ConfigDrivenFormComponent key={formKey} ref={setFormRefForFlavor} {...formProps} />
    </>
  );
};
